import { ComponentProps, ReactNode, useState } from 'react'
import { RefreshCw, TriangleAlert } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'
import AssignmentBoxConfigurator from './AssignmentBoxConfigurator'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import AssignmentEntityChip from './AssignmentEntityChip'
import { AssignmentViewMode } from './assignmentTimeline'
import Card from 'app/frontend/base/components/Card'
import RaidstratsContainer, { RaidstratsPlayer } from './RaidstratsContainer'
import {
  AssignmentPhaseTimestampBadge,
  AssignmentPhaseTimestampBadgeData,
} from './AssignmentPhaseTimestampBadge'
import AssignmentDescriptionContent, {
  AssignmentDescriptionControl,
} from './AssignmentDescriptionField'
import AssignmentNoteSettingsDialog from './AssignmentNoteSettingsDialog'

type Scope = {
  id: string
  label: string
  castIndexes: number[]
}

type NoteSettings = {
  message?: string
  ttsMessage?: string
}

type Props = {
  scope: Scope
  spellId: number
  castTimestamps: AssignmentPhaseTimestampBadgeData[]
  entitiesByGroup: RaidPlanAssignmentEntity[][]
  assignmentGroups: number
  canEdit: boolean
  paletteEntities: ComponentProps<
    typeof AssignmentBoxConfigurator
  >['paletteEntities']
  assignmentUsageCountByIdentity: Record<string, number>
  onSave: (entitiesByGroup: RaidPlanAssignmentEntity[][]) => void
  assignLabel: string
  raidPlanId: string | null
  raidPlanPlayers: RaidstratsPlayer[]
  abilityViewMode: AssignmentViewMode
  duplicateIdentities: Set<string>
  getEntityLabel: (entity: RaidPlanAssignmentEntity) => ReactNode
  groupLabel: (count: number) => string
  onAssignmentDialogOpenChange: (open: boolean) => void
  description?: string
  onDescriptionSave: (html: string) => Promise<void>
  noteSettingsByGroup: NoteSettings[]
  noteSettingsPlaceholder: { message: string; ttsMessage: string }
  onNoteSettingsSave: (boxIndex: number, next: NoteSettings) => void
}

const TeamPlanAssignmentScopeCard = ({
  scope,
  spellId,
  castTimestamps,
  entitiesByGroup,
  assignmentGroups,
  canEdit,
  paletteEntities,
  assignmentUsageCountByIdentity,
  onSave,
  assignLabel,
  raidPlanId,
  raidPlanPlayers,
  abilityViewMode,
  duplicateIdentities,
  getEntityLabel,
  groupLabel,
  onAssignmentDialogOpenChange,
  description,
  onDescriptionSave,
  noteSettingsByGroup,
  noteSettingsPlaceholder,
  onNoteSettingsSave,
}: Props) => {
  const [showRaidPlan, setShowRaidPlan] = useState(false)
  const [raidPlanReloadKey, setRaidPlanReloadKey] = useState(0)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const showNoteSettings = abilityViewMode !== 'all_together'
  return (
    <Card>
      <div className='mb-2 flex flex-wrap items-center gap-4'>
        <div className='font-medium text-sm'>{scope.label}</div>
        <div className='flex flex-wrap grow items-center gap-1 text-xs text-muted-foreground'>
          {castTimestamps.map((castTimestamp, index) => (
            <AssignmentPhaseTimestampBadge
              key={`${scope.id}:${index}`}
              phaseLabel={castTimestamp.phaseLabel}
              timeLabel={castTimestamp.timeLabel}
            />
          ))}
        </div>
        <div className='flex items-center gap-2'>
          <AssignmentDescriptionControl
            description={description}
            canEdit={canEdit}
            isEditing={isEditingDescription}
            onStartEdit={() => setIsEditingDescription(true)}
          />
          {raidPlanId ? (
            <>
              {showRaidPlan ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      className='h-7 w-7'
                      onClick={() => {
                        setShowRaidPlan(true)
                        setRaidPlanReloadKey((value) => value + 1)
                      }}
                    >
                      <RefreshCw className='h-3.5 w-3.5' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Fetch latest raid plan version
                  </TooltipContent>
                </Tooltip>
              ) : null}
              <Button
                type='button'
                variant='outline'
                size='custom'
                className='h-7 py-0.5 px-2'
                onClick={() => setShowRaidPlan((value) => !value)}
              >
                {showRaidPlan ? 'Hide visual' : 'View visual'}
              </Button>
            </>
          ) : null}
          {canEdit ? (
            <AssignmentBoxConfigurator
              spellId={spellId}
              entitiesByGroup={entitiesByGroup}
              paletteEntities={paletteEntities}
              assignmentUsageCountByIdentity={assignmentUsageCountByIdentity}
              canEdit={canEdit}
              onSave={onSave}
              onOpenChange={onAssignmentDialogOpenChange}
              trigger={
                <Button
                  type='button'
                  variant='outline'
                  size='custom'
                  className='h-7 py-0.5 px-2'
                >
                  {assignLabel}
                </Button>
              }
            />
          ) : null}
          {canEdit && showNoteSettings && assignmentGroups === 1 ? (
            <AssignmentNoteSettingsDialog
              label={scope.label}
              message={noteSettingsByGroup[0]?.message}
              ttsMessage={noteSettingsByGroup[0]?.ttsMessage}
              messagePlaceholder={noteSettingsPlaceholder.message}
              ttsPlaceholder={noteSettingsPlaceholder.ttsMessage}
              canEdit={canEdit}
              onSave={(next) => onNoteSettingsSave(0, next)}
            />
          ) : null}
        </div>
      </div>

      <AssignmentDescriptionContent
        description={description}
        isEditing={isEditingDescription}
        clearTarget='this cast'
        onStopEdit={() => setIsEditingDescription(false)}
        onSave={async (html) => {
          await onDescriptionSave(html)
          setIsEditingDescription(false)
        }}
        className='mb-2'
      />

      {showRaidPlan && raidPlanId ? (
        <div className='mb-3'>
          <RaidstratsContainer
            key={`${scope.id}-${raidPlanReloadKey}`}
            raidPlanId={raidPlanId}
            players={raidPlanPlayers}
          />
        </div>
      ) : null}

      <div
        className='grid gap-2'
        style={{
          gridTemplateColumns: `repeat(${Math.max(
            Math.min(assignmentGroups, 4),
            1,
          )}, minmax(0, 1fr))`,
        }}
      >
        {entitiesByGroup.map((groupEntities, boxIndex) => (
          <div key={boxIndex} className='mb-1'>
            {assignmentGroups > 1 ? (
              <div className='mb-1 pb-1 flex items-center gap-2'>
                <div className='text-xs'>{groupLabel(boxIndex + 1)}</div>
                {canEdit ? (
                  <AssignmentNoteSettingsDialog
                    label={`${scope.label} (${groupLabel(boxIndex + 1)})`}
                    message={noteSettingsByGroup[boxIndex]?.message}
                    ttsMessage={noteSettingsByGroup[boxIndex]?.ttsMessage}
                    messagePlaceholder={noteSettingsPlaceholder.message}
                    ttsPlaceholder={noteSettingsPlaceholder.ttsMessage}
                    canEdit={canEdit}
                    onSave={(next) => onNoteSettingsSave(boxIndex, next)}
                  />
                ) : null}
              </div>
            ) : null}
            {groupEntities.length === 0 ? (
              <div className='text-xs text-muted-foreground text-italic'>
                No entities assigned yet
              </div>
            ) : (
              <div className='flex flex-wrap items-center gap-1'>
                {groupEntities.map((entity, entityIndex) => {
                  const entityIdentity = getAssignmentEntityIdentity(entity)
                  const showDuplicateWarning =
                    entity.kind !== 'by_role' &&
                    entity.kind !== 'by_class' &&
                    abilityViewMode !== 'all_separate' &&
                    duplicateIdentities.has(entityIdentity)
                  return (
                    <div
                      key={`${entity.kind}-${entityIndex}-${entity.kind}`}
                      className='inline-flex items-center'
                    >
                      <AssignmentEntityChip
                        index={entityIndex}
                        label={getEntityLabel(entity)}
                        warning={showDuplicateWarning}
                        endSlot={
                          showDuplicateWarning ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className='inline-flex items-center text-warning-foreground'>
                                  <TriangleAlert className='h-3.5 w-3.5' />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                This entity is in multiple groups
                              </TooltipContent>
                            </Tooltip>
                          ) : undefined
                        }
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  )
}

export default TeamPlanAssignmentScopeCard
